import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  ArrowLeft, 
  FileSpreadsheet, 
  RefreshCw, 
  Download,
  Search,
  X,
  Check,
  PlusCircle,
  Merge,
  Package,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { handleSupabaseUniqueConstraintError } from '@/lib/dbUtils';
import { StatusBadge } from '@/components/common/StatusBadge';

import { 
  IngramProduct, 
  PaginationInfo, 
  SubscriptionPlanFamily, 
  Vendor,
  ImportProgressItem,
  MappedPlanData
} from './ImportSubscriptionsPlans.types';
import {
  getVendorId,
  mapIngramProductToPlanData,
  processAndInsertPlan,
  syncNewProductWithHubspot,
} from './ImportSubscriptionsPlans.helpers';

// URL da Edge Function para atualizar produto no HubSpot
import { getEdgeFunctionUrl } from '@/config/environment';
const UPDATE_HUBSPOT_PRODUCT_URL = getEdgeFunctionUrl('update-product-hubspot');

const ImportSubscriptionsPlans = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [products, setProducts] = useState<IngramProduct[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    offset: 0,
    limit: 20,
    total: 0
  });
  
  // Filtros
  const [vendorFilter, setVendorFilter] = useState<string>('all'); // Changed to 'all'
  const [nameFilter, setNameFilter] = useState<string>('');
  const [mpnFilter, setMpnFilter] = useState<string>('');
  const [importedFilter, setImportedFilter] = useState<string>('all'); // 'all', 'imported', 'not_imported'
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);

  // Move all useState declarations from outside the component to here
  // Adicionar novos estados para o modal de progresso
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgressItem[]>([]);
  const [importComplete, setImportComplete] = useState(false);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
  // Adicionar estado para controlar a exibição dos detalhes de erro
  const [selectedProgressItem, setSelectedProgressItem] = useState<number | null>(null);

  const [planFamilies, setPlanFamilies] = useState<SubscriptionPlanFamily[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<IngramProduct[]>([]);
  const [showNewFamilyForm, setShowNewFamilyForm] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyDescription, setNewFamilyDescription] = useState('');
  const [creatingFamily, setCreatingFamily] = useState(false);
  const [loadingFamilies, setLoadingFamilies] = useState(false);

  // Vendors state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Estados para o modal de progresso de importação
  const [importedCount, setImportedCount] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);
  const [currentProductName, setCurrentProductName] = useState('');
  const [showProgressDialog, setShowProgressDialog] = useState(false);

  // Add a state for unit_measure selection
  const [selectedUnitMeasure, setSelectedUnitMeasure] = useState<string>('user');

  // State for page input
  const [pageInput, setPageInput] = useState<string>('');

  // Estados para o modal de mesclagem
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedProductForMerge, setSelectedProductForMerge] = useState<IngramProduct | null>(null);
  const [hubspotProductId, setHubspotProductId] = useState('');
  const [isMerging, setIsMerging] = useState(false);
  const [selectedMergeFamily, setSelectedMergeFamily] = useState<string | null>(null);

  // Função para buscar produtos da Ingram Micro através da view formatada
  const fetchProducts = async (page = 1) => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      
      // Construir a query base usando apenas os campos necessários da view
      let query = supabase
        .from('subscription_plans_ingram_formated')
        .select(`
          id,
          mpn,
          vendor,
          vendor_id,
          vendor_name,
          product_id,
          mpn_custom,
          name,
          description,
          minimum_quantity,
          maximum_quantity,
          billing_period_type,
          billing_period_duration,
          subscription_period_type,
          subscription_period_duration,
          price_type,
          price_amount,
          price_currency,
          cost_type,
          cost_amount,
          cost_currency,
          external_id,
          created_at
        `, { count: 'exact' });
      
      // Aplicar filtros
      if (vendorFilter && vendorFilter !== 'all') {
        query = query.eq('vendor_id', vendorFilter);
      }
      
      if (nameFilter) {
        query = query.ilike('name', `%${nameFilter}%`);
      }
      
      if (mpnFilter) {
        query = query.ilike('mpn', `%${mpnFilter}%`);
      }
      
      // Aplicar filtro de importação
      if (importedFilter === 'imported') {
        query = query.not('external_id', 'is', null);
      } else if (importedFilter === 'not_imported') {
        query = query.is('external_id', null);
      }
      
      // Aplicar paginação
      const { data, error, count } = await query
        .range(offset, offset + itemsPerPage - 1)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
              // Transformar os dados do formato do Supabase para o formato esperado pela aplicação
        if (!import.meta.env.PROD) console.log('Dados brutos da view antes da transformação:', data);
        const transformedProducts = data.map((item, idx) => {
          if (!import.meta.env.PROD) console.log(`Item ${idx} description:`, item.description);
        // Criar estrutura de preços e custos baseada nos novos campos da view
        const prices = [{
          currency: item.price_currency,
          amount: item.price_amount?.toString() || '0',
          type: item.price_type || 'recurring',
          model: 'flat', // Default value since price_model doesn't exist
          lowerLimit: 1
        }];
        
        const costs = [{
          currency: item.cost_currency,
          amount: item.cost_amount?.toString() || '0',
          type: item.cost_type || 'recurring',
          model: 'flat', // Default value since cost_model doesn't exist
          lowerLimit: 1
        }];

        // Criar estrutura de períodos baseada nos novos campos
        const billingPeriod = {
          type: item.billing_period_type || 'month',
          duration: parseInt(item.billing_period_duration) || 1
        };
        
        const subscriptionPeriod = {
          type: item.subscription_period_type || 'month',
          duration: parseInt(item.subscription_period_duration) || 1
        };

        // Verificar se o produto já foi importado baseado no external_id
        const isImported = Boolean(item.external_id);

        // Criar objeto no formato esperado
        return {
          mpn: item.mpn,
          mpn_custom: item.mpn_custom,
          vendor: item.vendor_name || item.vendor, // Usar vendor_name se disponível
          id: item.product_id || '',
          name: item.name,
          description: item.description, // Usar o valor direto da view
          minimumQuantity: item.minimum_quantity?.toString() || '1',
          maximumQuantity: item.maximum_quantity?.toString() || '9999',
          prices,
          costs,
          billingPeriod,
          subscriptionPeriod,
          billingModel: 'on creation', // Valor padrão
          dependsOn: [],
          coterming: { required: 'false', type: '' },
          selected: false,
          uniqueId: `${item.id}-${idx}`, // Usar o ID da view para melhor identificação
          external_id: item.external_id,
          isImported
        } as IngramProduct;
      });
      
      setProducts(transformedProducts);
      setPagination({
        offset,
        limit: itemsPerPage,
        total: count || 0
      });
      setCurrentPage(page);
      

    } catch (error: any) {
      console.error('Erro ao buscar produtos:', error);
      toast.error(`Falha ao carregar produtos: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch plan families
  const fetchPlanFamilies = async () => {
    setLoadingFamilies(true);
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('*')
        .order('name');

      if (error) throw error;
      setPlanFamilies(data || []);
    } catch (error: any) {
      console.error('Error fetching plan families:', error);
      toast.error(`Failed to load plan families: ${error.message}`);
    } finally {
      setLoadingFamilies(false);
    }
  };

  // Fetch vendors
  const fetchVendors = async () => {
    setLoadingVendors(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, name, status')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setVendors(data || []);
    } catch (error: any) {
      console.error('Error fetching vendors:', error);
      toast.error(`Failed to load vendors: ${error.message}`);
    } finally {
      setLoadingVendors(false);
    }
  };

  // Carregar produtos ao montar o componente
  useEffect(() => {
    fetchProducts(1);
    fetchPlanFamilies();
    fetchVendors();
  }, []);

  // Função para selecionar/deselecionar todos os produtos (apenas os não importados)
  const handleSelectAll = (checked: boolean) => {
    setProducts(products.map(product => ({
      ...product,
      selected: product.isImported ? false : checked
    })));
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    if (checked) {
      setSelectedProducts(products.filter(p => !p.isImported));
    } else {
      setSelectedProducts([]);
    }
  };

  // Função para selecionar/deselecionar um produto (apenas se não foi importado)
  const handleSelectProduct = (id: string, checked: boolean) => {
    console.log(`Selecting product with ID: ${id}, checked: ${checked}`);
    
    // Encontrar o produto para verificar se já foi importado
    const product = products.find(p => p.uniqueId === id);
    if (product?.isImported) {
      console.log('Produto já importado, não pode ser selecionado');
      return;
    }
    
    // Atualizar o estado dos produtos
    const updatedProducts = products.map(product => 
      product.uniqueId === id ? { ...product, selected: checked } : product
    );
    
    setProducts(updatedProducts);
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    setSelectedProducts(updatedProducts.filter(p => p.selected && !p.isImported));
  };

  // Função para alternar a seleção de um produto ao clicar na linha (apenas se não foi importado)
  const handleRowClick = (id: string) => {
    console.log(`Row click for product with ID: ${id}`);
    
    // Encontrar o produto para verificar se já foi importado
    const product = products.find(p => p.uniqueId === id);
    if (product?.isImported) {
      console.log('Produto já importado, não pode ser selecionado');
      return;
    }
    
    // Alternar a seleção do produto
    const updatedProducts = products.map(product => 
      product.uniqueId === id ? { ...product, selected: !product.selected } : product
    );
    
    setProducts(updatedProducts);
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    setSelectedProducts(updatedProducts.filter(p => p.selected && !p.isImported));
    
    console.log('Updated products after row click:', updatedProducts.filter(p => p.selected && !p.isImported).length);
  };

  // Função para selecionar múltiplos produtos com Shift+Click (apenas os não importados)
  const handleShiftClick = (index: number) => {
    console.log(`Shift click at index: ${index}`);
    
    // Verificar se o produto atual pode ser selecionado
    if (products[index]?.isImported) {
      console.log('Produto já importado, não pode ser selecionado');
      return;
    }
    
    // Se não houver seleção anterior, seleciona apenas o item atual
    if (lastSelectedIndex === -1) {
      handleRowClick(products[index].uniqueId);
      setLastSelectedIndex(index);
      return;
    }

    // Determinar o intervalo de seleção
    const start = Math.min(lastSelectedIndex, index);
    const end = Math.max(lastSelectedIndex, index);
    console.log(`Selecting range from ${start} to ${end}`);

    // Atualizar o estado dos produtos no intervalo (apenas os não importados)
    const updatedProducts = products.map((product, i) => {
      if (i >= start && i <= end && !product.isImported) {
        return { ...product, selected: true };
      }
      return product;
    });
    
    setProducts(updatedProducts);
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    setSelectedProducts(updatedProducts.filter(p => p.selected && !p.isImported));
    
    console.log('Updated products after shift click:', updatedProducts.filter(p => p.selected && !p.isImported).length);

    // Atualizar o último índice selecionado
    setLastSelectedIndex(index);
  };

  // Estado para rastrear o último índice selecionado (para seleção com Shift)
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);

  // Função para aplicar filtros
  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchProducts(1);
  };

  // Função para limpar filtros
  const handleClearFilters = () => {
    setVendorFilter('all'); // Reset to 'all'
    setNameFilter('');
    setMpnFilter('');
    setImportedFilter('all');
    setCurrentPage(1);
    fetchProducts(1);
  };

  // Função para alterar o número de itens por página
  const handleItemsPerPageChange = (value: string) => {
    const newSize = parseInt(value);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    // Atualizamos a paginação e buscamos os produtos novamente
    setPagination(prev => ({
      ...prev,
      limit: newSize
    }));
    fetchProducts(1);
  };

  // Função para navegar entre páginas
  const handlePageChange = (page: number) => {
    if (page < 1 || page > Math.ceil(pagination.total / pagination.limit)) {
      return;
    }
    fetchProducts(page);
  };

  // Função para ir para uma página específica digitada
  const handleGoToPage = () => {
    const page = parseInt(pageInput);
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    
    if (isNaN(page) || page < 1 || page > totalPages) {
      toast.error(`Por favor, digite um número entre 1 e ${totalPages}`);
      return;
    }
    
    handlePageChange(page);
    setPageInput('');
  };

  // Função para lidar com Enter no input da página
  const handlePageInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  // Função para renderizar a paginação
  const renderPagination = () => {
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    
    if (totalPages <= 1) {
      return null;
    }
    
    return (
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Página {currentPage} de {totalPages}</span>
          <span>({pagination.total} itens no total)</span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Page input section */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Ir para página:</span>
            <Input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyPress={handlePageInputKeyPress}
              className="w-16 h-8 text-center"
              placeholder={currentPage.toString()}
            />
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleGoToPage}
              disabled={!pageInput}
            >
              Ir
            </Button>
          </div>

          {/* Standard pagination */}
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              
              {currentPage > 2 && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                </PaginationItem>
              )}
              
              {currentPage > 3 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              
              {currentPage > 1 && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(currentPage - 1)}>
                    {currentPage - 1}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              <PaginationItem>
                <PaginationLink isActive>{currentPage}</PaginationLink>
              </PaginationItem>
              
              {currentPage < totalPages && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(currentPage + 1)}>
                    {currentPage + 1}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              {currentPage < totalPages - 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              
              {currentPage < totalPages - 1 && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    );
  };

  // Função para obter o preço formatado
  const getFormattedPrice = (product: IngramProduct) => {
    if (product.prices.length === 0) return 'N/A';
    const price = parseFloat(product.prices[0].amount);
    
    // Formatar o preço no padrão brasileiro (R$ 0.000,00)
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Função para obter o período formatado
  const getFormattedPeriod = (product: IngramProduct) => {
    // Determinar o período de cobrança
    let period = '';
    
    if (product.billingPeriod.type === 'monthly' || product.billingPeriod.type === 'month') {
      period = 'Mês';
    } else if (product.billingPeriod.type === 'yearly' || product.billingPeriod.type === 'annually' || product.billingPeriod.type === 'year') {
      period = 'Ano';
    } else if (product.billingPeriod.type === 'quarterly') {
      period = 'Trimestre';
    } else if (product.billingPeriod.type === 'semiannually') {
      period = 'Semestre';
    } else if (product.billingPeriod.type === 'one-time') {
      period = 'Único';
    } else {
      period = product.billingPeriod.type;
    }
    
    return period;
  };

  // Função para formatar tipo de período em português
  const formatPeriodType = (periodType: string) => {
    if (periodType === 'monthly' || periodType === 'month') {
      return 'Mensal';
    } else if (periodType === 'yearly' || periodType === 'annually' || periodType === 'year') {
      return 'Anual';
    } else if (periodType === 'quarterly') {
      return 'Trimestral';
    } else if (periodType === 'semiannually') {
      return 'Semestral';
    } else if (periodType === 'one-time') {
      return 'Único';
    } else {
      return periodType || '-';
    }
  };

  // Função para formatar período com duração - exemplo: "Mensal (1)"
  const formatPeriodWithDuration = (periodType: string, duration: number) => {
    const formattedType = formatPeriodType(periodType);
    return `${formattedType} (${duration || 1})`;
  };

  // Function to handle import button click
  const handleImportClick = () => {
    const availableProducts = selectedProducts.filter(p => !p.isImported);
    if (availableProducts.length === 0) {
      toast.error('Selecione pelo menos um produto não importado para importar');
      return;
    }
    
    setShowFamilyModal(true);
  };

  // Função para importar produtos com a família selecionada
  const handleImportWithFamily = async () => {
    if (!selectedFamily) {
      toast.error('Por favor, selecione uma família de planos');
      return;
    }

    const productsToImport = products.filter(p => p.selected && !p.isImported);
    if (productsToImport.length === 0) {
       toast.error('Nenhum produto disponível para importar.');
       return;
    }

    setShowFamilyModal(false);
    setIsImporting(true);
    setShowProgressModal(true);
    setImportComplete(false);
    setImportProgress(productsToImport.map(p => ({
        name: p.name,
        sku: p.mpn_custom || p.mpn,
        status: 'pending',
        message: ''
    })));
    setCurrentProcessingIndex(0);

    let successCount = 0;
    let errorCount = 0;

    try {
      const vendorId = productsToImport.length > 0 ? await getVendorId(productsToImport[0].vendor) : null;

      for (let i = 0; i < productsToImport.length; i++) {
        const product = productsToImport[i];
        setCurrentProcessingIndex(i);
        const updateProgress = (status: 'pending' | 'success' | 'error', message: string) => {
          setImportProgress(prev => {
            const newProgress = [...prev];
            if (newProgress[i]) {
              newProgress[i] = { ...newProgress[i], status, message };
            }
            return newProgress;
          });
        };

        try {
          if (product.external_id) {
            updateProgress('error', 'Produto já existe (ignorado)');
            errorCount++;
            continue;
          }

          updateProgress('pending', 'Mapeando dados do produto...');
          const planData = mapIngramProductToPlanData(product, selectedFamily, vendorId);

          updateProgress('pending', 'Inserindo plano na base de dados...');
          const { planId, finalPlanData } = await processAndInsertPlan(planData);

          updateProgress('pending', 'Sincronizando com HubSpot...');
          const syncResult = await syncNewProductWithHubspot(planId, finalPlanData);

          if (syncResult.success) {
            updateProgress('success', 'Importado com sucesso.');
            successCount++;
          } else {
            throw new Error(syncResult.message);
          }
        } catch (error: any) {
          console.error(`Erro ao importar plano ${product.name}:`, error);
          const errorMessage = error.cause?.code === '23505' ? 'Plano duplicado (nome/SKU)' : error.message;
          updateProgress('error', `Erro: ${errorMessage}`);
          errorCount++;
        }
      }

      if (errorCount > 0 && successCount > 0) {
        toast.warning(`Importação concluída: ${successCount} com sucesso, ${errorCount} com falha.`);
      } else if (errorCount > 0) {
        toast.error(`Importação concluída com ${errorCount} falha(s).`);
      } else if (successCount > 0) {
        toast.success(`Importação concluída: ${successCount} produto(s) importado(s) com sucesso.`);
      } else {
        toast.info('Nenhum produto foi importado.');
      }

    } catch (error: any) {
      console.error('Erro CRÍTICO GERAL na importação:', error);
      toast.error(`Falha CRÍTICA na importação: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsImporting(false);
      setImportComplete(true);
      setProducts(prev => prev.map(p => ({ ...p, selected: false })));
      setSelectedProducts([]);
      setTimeout(() => fetchProducts(currentPage), 2000);
    }
  };

  // Function to create a new subscription plan family
  const handleCreateFamily = async () => {
    if (!newFamilyName.trim()) {
      toast.error('O nome da família é obrigatório');
      return;
    }
    
    setCreatingFamily(true);
    
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .insert({
          name: newFamilyName.trim(),
          description: newFamilyDescription.trim() || null
        })
        .select('*')
        .single();
        
      if (error) throw error;
      
      // Add the new family to the list and select it
      setPlanFamilies([...planFamilies, data]);
      setSelectedFamily(data.id);
      
      // Reset the form
      setNewFamilyName('');
      setNewFamilyDescription('');
      setShowNewFamilyForm(false);
      
      toast.success('Família de planos criada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao criar família de planos:', error);
      toast.error(`Falha ao criar família de planos: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setCreatingFamily(false);
    }
  };

  // Função para atualizar o item selecionado para mostrar detalhes
  const handleProgressItemClick = (index: number) => {
    if (selectedProgressItem === index) {
      setSelectedProgressItem(null);
    } else {
      setSelectedProgressItem(index);
    }
  };

  // Função para abrir modal de mesclagem
  const handleMergeClick = (product: IngramProduct) => {
    setSelectedProductForMerge(product);
    setHubspotProductId('');
    setSelectedMergeFamily(null);
    setShowMergeModal(true);
  };

  // Função para mesclar produto com HubSpot existente
  const handleMergeProduct = async () => {
    if (!selectedProductForMerge || !hubspotProductId.trim()) {
      toast.error('Por favor, informe o ID do produto no HubSpot');
      return;
    }

    setIsMerging(true);
    
    try {
      // 1. Buscar vendor_id baseado no nome do vendor
      const vendorId = await getVendorId(selectedProductForMerge.vendor);

      // 2. Processar e inserir o plano
      const planData = mapIngramProductToPlanData(selectedProductForMerge, selectedMergeFamily, vendorId);
      const planId = await processAndInsertPlan(planData);
      
      // 3. Inserir ID do HubSpot fornecido pelo usuário
      const { error: hubspotIdError } = await supabase
        .from('external_ids')
        .insert({
          entity_name: 'subscription_plans',
          entity_id: planId,
          system_name: 'hubspot',
          external_id: hubspotProductId.trim()
        });
        
      if (hubspotIdError) throw hubspotIdError;
      
      // 4. Chamar a edge function de atualização do HubSpot
      const { data: hubspotViewData, error: hubspotViewError } = await supabase
        .from('update_hubspot_product')
        .select('*')
        .eq('id', planId)
        .single();

      if (hubspotViewError || !hubspotViewData) {
        throw new Error('Não foi possível obter dados para sincronização com HubSpot');
      }

      const hubspotPayload = {
        productId: hubspotViewData.productid,
        name: hubspotViewData.name || '',
        description: hubspotViewData.description || '',
        hs_sku: hubspotViewData.hs_sku || '',
        hs_price_brl: hubspotViewData.hs_price_brl?.toString() || '0.00',
        hs_product_type: "license", // Simplificado
        recurringbillingfrequency: normalizeBillingFrequency(hubspotViewData.recurringbillingfrequency || 'month', false)
      };

      const response = await fetch(UPDATE_HUBSPOT_PRODUCT_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(hubspotPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro da edge function: ${response.status} - ${errorText}`);
      }
      
      toast.success(`Produto ${planData.name} mesclado com sucesso!`);
      setShowMergeModal(false);
      fetchProducts(currentPage);
      
    } catch (error: any) {
      console.error('Erro ao mesclar produto:', error);
      toast.error(`Erro ao mesclar produto: ${error.message}`);
    } finally {
      setIsMerging(false);
    }
  };

  // Monitorar mudanças na lista de produtos selecionados
  useEffect(() => {
    console.log(`Número de produtos selecionados: ${selectedProducts.length}`);
  }, [selectedProducts]);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Background Image */}
        <div 
          className="relative overflow-hidden" 
          style={{ 
            backgroundColor: '#1F1746',
            backgroundImage: 'url(/elemento-01.svg)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Importar Planos de Assinatura</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Importe planos de assinatura do catálogo Ingram Cloud Marketplace
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => navigate('/subscription-plans')} 
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <Button 
                  size="default" 
                  onClick={handleImportClick}
                  disabled={selectedProducts.filter(p => !p.isImported).length === 0}
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Importar Selecionados ({selectedProducts.filter(p => !p.isImported).length})
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">

            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Filtrar Produtos</CardTitle>
                <CardDescription>
                  Filtre os produtos por fornecedor, nome, número de série (MPN) ou status de importação
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                  <div className="flex-1">
                    <Label htmlFor="vendor">Fornecedor</Label>
                    <Select value={vendorFilter} onValueChange={setVendorFilter}>
                      <SelectTrigger id="vendor" className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                        <SelectValue placeholder="Todos os fornecedores" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os fornecedores</SelectItem>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="name">Nome do Produto</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="name"
                        placeholder="Buscar por nome"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="mpn">MPN</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="mpn"
                        placeholder="Buscar por MPN"
                        value={mpnFilter}
                        onChange={(e) => setMpnFilter(e.target.value)}
                        className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="imported">Status</Label>
                    <Select value={importedFilter} onValueChange={setImportedFilter}>
                      <SelectTrigger id="imported" className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                        <SelectValue placeholder="Todos os planos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os planos</SelectItem>
                        <SelectItem value="not_imported">Não importados</SelectItem>
                        <SelectItem value="imported">Importados</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleClearFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Limpar Filtros
                  </Button>
                  <Button onClick={handleApplyFilters}>
                    <Search className="h-4 w-4 mr-2" />
                    Buscar
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Produtos Disponíveis</CardTitle>
                <CardDescription>
                  Selecione os produtos que deseja importar para o catálogo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30px]">
                          <Checkbox
                            checked={products.length > 0 && products.filter(p => !p.isImported).every(p => p.selected)}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead>Período de Cobrança</TableHead>
                        <TableHead>Período de Assinatura</TableHead>
                        <TableHead>Preço</TableHead>
                        <TableHead className="w-[50px] text-center">Importado</TableHead>
                        <TableHead className="w-[50px] text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8">
                            <div className="flex flex-col items-center">
                              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
                              <p className="text-muted-foreground">Carregando produtos...</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : products.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            Nenhum produto encontrado. Tente ajustar os filtros.
                          </TableCell>
                        </TableRow>
                      ) : (
                        products.map((product, index) => (
                          <TableRow 
                            key={product.uniqueId}
                            className={`cursor-pointer ${product.isImported ? 'opacity-60 bg-gray-50' : ''}`}
                            onClick={() => !product.isImported && handleRowClick(product.uniqueId)}
                          >
                            <TableCell onClick={(e) => {
                              e.stopPropagation();
                              if (!product.isImported) {
                                handleSelectProduct(product.uniqueId, !product.selected);
                              }
                            }}>
                              <Checkbox 
                                checked={product.selected}
                                disabled={product.isImported}
                                onCheckedChange={(checked) => {
                                  if (!product.isImported) {
                                    handleSelectProduct(product.uniqueId, checked as boolean);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-extrabold">
                              <div>
                                <div>{product.name}</div>
                                <div className="text-xs text-muted-foreground mt-1">{product.mpn}</div>
                              </div>
                            </TableCell>
                            <TableCell>{product.vendor}</TableCell>
                            <TableCell>{formatPeriodWithDuration(product.billingPeriod.type, product.billingPeriod.duration)}</TableCell>
                            <TableCell>{formatPeriodWithDuration(product.subscriptionPeriod.type, product.subscriptionPeriod.duration)}</TableCell>
                            <TableCell>{getFormattedPrice(product)}</TableCell>
                            <TableCell className="p-0">
                              <div className="flex items-center justify-center h-[52px]">
                                {product.isImported ? (
                                  <Badge variant="secondary" className="bg-green-100 text-green-800 w-8 h-8 flex items-center justify-center p-0">
                                    <Check className="h-4 w-4" />
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="w-8 h-8 flex items-center justify-center p-0">
                                    <X className="h-4 w-4" />
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="p-0">
                              <div className="flex items-center justify-center h-[52px]">
                                {!product.isImported && (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMergeClick(product);
                                    }}
                                    title="Mesclar"
                                    className="w-8 h-8"
                                  >
                                    <Merge className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                {!isLoading && products.length > 0 && renderPagination()}
              </CardContent>
            </Card>

            {/* Bottom Spacing */}
            <div className="h-16" />
          </div>
        </div>

      {/* Family Selection Dialog */}
      <Dialog open={showFamilyModal} onOpenChange={setShowFamilyModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Selecionar Família de Planos</DialogTitle>
            <DialogDescription>
              Escolha a família para os {selectedProducts.length} planos selecionados ou crie uma nova família.
            </DialogDescription>
          </DialogHeader>
          
          {!showNewFamilyForm ? (
            <>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="family">Família de Planos</Label>
                  <Select value={selectedFamily || ''} onValueChange={setSelectedFamily}>
                    <SelectTrigger id="family">
                      <SelectValue placeholder="Selecione uma família" />
                    </SelectTrigger>
                    <SelectContent>
                      {planFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id}>
                          {family.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    variant="link"
                    onClick={() => setShowNewFamilyForm(true)}
                    className="p-0 h-auto mt-2"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Criar nova família
                  </Button>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowFamilyModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleImportWithFamily}
                  disabled={!selectedFamily}
                >
                  Importar Planos
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="familyName">Nome da Família</Label>
                  <Input
                    id="familyName"
                    value={newFamilyName}
                    onChange={(e) => setNewFamilyName(e.target.value)}
                    placeholder="ex: Microsoft 365"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="familyDescription">Descrição</Label>
                  <Textarea
                    id="familyDescription"
                    value={newFamilyDescription}
                    onChange={(e) => setNewFamilyDescription(e.target.value)}
                    placeholder="Descrição opcional para a família de planos"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewFamilyForm(false)}>
                  Voltar
                </Button>
                <Button 
                  onClick={handleCreateFamily}
                  disabled={!newFamilyName || creatingFamily}
                >
                  {creatingFamily ? 'Criando...' : 'Criar Família'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mesclar com Produto HubSpot</DialogTitle>
            <DialogDescription>
              O produto "{selectedProductForMerge?.name}" será importado e mesclado com um produto existente no HubSpot.
              Por favor, informe o ID do produto no HubSpot.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="merge-family">Família de Planos</Label>
              <Select value={selectedMergeFamily || ''} onValueChange={setSelectedMergeFamily}>
                <SelectTrigger id="merge-family">
                  <SelectValue placeholder="Selecione uma família (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {planFamilies.map((family) => (
                    <SelectItem key={family.id} value={family.id}>
                      {family.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="hubspot-id">ID do Produto no HubSpot</Label>
              <Input
                id="hubspot-id"
                value={hubspotProductId}
                onChange={(e) => setHubspotProductId(e.target.value)}
                placeholder="Digite o ID do produto no HubSpot"
                disabled={isMerging}
              />
              <p className="text-sm text-muted-foreground">
                Este ID será usado para conectar o plano importado ao produto existente no HubSpot.
              </p>
            </div>
            
            {selectedProductForMerge && (
              <div className="bg-muted/40 rounded-lg p-3">
                <h4 className="font-extrabold mb-2">Detalhes do Produto:</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Nome:</strong> {selectedProductForMerge.name}</p>
                  <p><strong>MPN:</strong> {selectedProductForMerge.mpn}</p>
                  <p><strong>Fornecedor:</strong> {selectedProductForMerge.vendor}</p>
                  <p><strong>Preço:</strong> {getFormattedPrice(selectedProductForMerge)}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowMergeModal(false)}
              disabled={isMerging}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleMergeProduct}
              disabled={!hubspotProductId.trim() || isMerging}
            >
              {isMerging ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Mesclando...
                </>
              ) : (
                <>
                  <Merge className="h-4 w-4 mr-2" />
                  Mesclar Produto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={showProgressModal} onOpenChange={(open) => {
        // Only allow closing if import is complete
        if (importComplete) setShowProgressModal(open);
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Importação em Andamento</DialogTitle>
            <DialogDescription>
              {importComplete 
                ? `Importação concluída. ${importProgress.filter(p => p.status === 'success').length} de ${importProgress.length} planos importados com sucesso.`
                : `Importando plano ${currentProcessingIndex + 1} de ${importProgress.length}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[350px] overflow-y-auto">
            {importProgress.map((item, index) => (
              <div 
                key={index} 
                className={cn(
                  "py-2 px-3 mb-2 rounded-md border flex items-center justify-between cursor-pointer",
                  item.status === 'success' ? 'bg-green-50 border-green-200' : 
                  item.status === 'error' ? 'bg-red-50 border-red-200' : 
                  'bg-yellow-50 border-yellow-200'
                )}
                onClick={() => handleProgressItemClick(index)}
              >
                <div className="flex flex-col">
                  <span className="font-extrabold">{item.name}</span>
                  <span className="text-xs text-muted-foreground">SKU: {item.sku}</span>
                  
                  {selectedProgressItem === index && item.status === 'error' && item.message && (
                    <div className="mt-2 text-xs text-red-700 bg-red-50 p-2 rounded-md border border-red-200">
                      {item.message}
                    </div>
                  )}
                </div>
                <div>
                  {item.status === 'success' && <Check className="h-5 w-5 text-green-600" />}
                  {item.status === 'error' && <X className="h-5 w-5 text-red-600" />}
                  {item.status === 'pending' && <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            {importComplete && (
              <Button onClick={() => {
                setShowProgressModal(false);
                setImportComplete(false);
                setImportProgress([]);
                fetchProducts(); // Recarrega a lista de produtos
              }}>
                Concluir
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportSubscriptionsPlans; 